import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Info, AlertTriangle, CheckCircle2, XCircle, Clock, BellRing, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TYPE_CONFIG = {
  success: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
  warning: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  return `hace ${Math.floor(hrs / 24)}d`;
}

async function listenSSE(url, token, onNotification) {
  let response;
  try {
    response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
      signal: AbortSignal.timeout(5 * 60 * 1000), // 5 min timeout
    });
  } catch (err) {
    // Connection failed - will retry
    return;
  }
  if (!response.ok) return;
  const reader = response.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'notification') onNotification(data);
        } catch (parseErr) {
          // ignore malformed SSE line
        }
      }
    }
  } catch (readErr) {
    // stream ended
  } finally {
    reader.releaseLock();
  }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const panelRef = useRef(null);
  const queryClient = useQueryClient();

  const requestBrowserPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setBrowserPermission(result);
  }, []);

  const { data: unreadData } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: () => api.get('/notifications/unread-count').then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: notifsData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications?limit=20').then(r => r.data),
    enabled: open,
    refetchInterval: open ? 15000 : false,
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  // SSE subscription for real-time notifications
  const handleNotification = useCallback((data) => {
    toast(data.title, { description: data.message, duration: 6000 });
    queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    // Browser push notification
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(data.title || 'SCMejora', {
          body: data.message || '',
          icon: '/favicon.ico',
          tag: 'scmejora-notif',
        });
      } catch (_e) { /* silent */ }
    }
  }, [queryClient]);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const url = `${backendUrl}/api/notifications/stream`;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      listenSSE(url, token, handleNotification).then(() => {
        // Reconnect after 5s if not cancelled
        if (!cancelled) setTimeout(connect, 5000);
      });
    };

    connect();
    return () => { cancelled = true; };
  }, [handleNotification]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const unreadCount = unreadData?.unread_count || 0;
  const notifications = notifsData?.notifications || [];

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="notification-bell"
        className="relative p-2 rounded-md hover:bg-muted transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-border z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold text-sm">Notificaciones</span>
              {unreadCount > 0 && (
                <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {unreadCount} nuevas
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                <Bell className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.info;
                const Icon = cfg.icon;
                return (
                  <div
                    key={notif._id}
                    data-testid={`notification-${notif._id}`}
                    onClick={() => !notif.read && markReadMutation.mutate(notif._id)}
                    className={`flex gap-3 px-4 py-3 border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-muted/40 ${!notif.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center mt-0.5`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium leading-tight ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {timeAgo(notif.created_at)}
                        {notif.task_codigo && (
                          <span className="ml-1 font-mono bg-muted px-1 rounded">{notif.task_codigo}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Browser notifications footer */}
          <div className="border-t border-border px-4 py-2.5 bg-muted/20">
            {browserPermission === 'unsupported' ? (
              <p className="text-[11px] text-muted-foreground">Tu navegador no soporta notificaciones push</p>
            ) : browserPermission === 'granted' ? (
              <div className="flex items-center gap-1.5 text-[11px] text-green-600">
                <BellRing className="w-3 h-3" />
                <span>Notificaciones de escritorio activas</span>
              </div>
            ) : browserPermission === 'denied' ? (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <BellOff className="w-3 h-3" />
                <span>Bloqueadas — actívalas en la config. del navegador</span>
              </div>
            ) : (
              <button
                onClick={requestBrowserPermission}
                data-testid="enable-browser-notifications"
                className="flex items-center gap-1.5 text-[11px] text-primary hover:underline"
              >
                <Bell className="w-3 h-3" />
                Activar notificaciones de escritorio
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
